import { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import ExcelJS from "exceljs";
import api from '../config/api';

export default function JournalReports() {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [isLoading, setIsLoading] = useState(false);
  const [accountCodes, setAccountCodes] = useState([]);

  function isLeapYear(year) {
    return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
  }

  const getMonths = (year) => [
    { name: "January", start: "01", end: "31" },
    { name: "February", start: "02", end: isLeapYear(year) ? "29" : "28" },
    { name: "March", start: "03", end: "31" },
    { name: "April", start: "04", end: "30" },
    { name: "May", start: "05", end: "31" },
    { name: "June", start: "06", end: "30" },
    { name: "July", start: "07", end: "31" },
    { name: "August", start: "08", end: "31" },
    { name: "September", start: "09", end: "30" },
    { name: "October", start: "10", end: "31" },
    { name: "November", start: "11", end: "30" },
    { name: "December", start: "12", end: "31" },
  ];

  const exportToXLSX = async (transactions, monthName) => {
    const sorted = [...transactions].sort((a, b) => new Date(b.transaction_date) - new Date(a.transaction_date));

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Journal");

    sheet.columns = [
      { header: "TANGGAL", key: "tanggal", width: 12 },
      { header: "NAMA PERKIRAAN", key: "nama_perkiraan", width: 24 },
      { header: "KETERANGAN", key: "keterangan", width: 40 },
      { header: "KODE", key: "kode", width: 12 },
      { header: "DEBIT", key: "debit", width: 15 },
      { header: "KREDIT", key: "kredit", width: 15 },
    ];

    // Header style
    sheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD966" }, // Yellow
      };
      cell.alignment = { vertical: "middle", horizontal: "center" };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    let totalDebit = 0;
    let totalCredit = 0;

    sorted.forEach((tx, index) => {
      const account = accountCodes.find((ac) => ac.code === tx.account_code);
      const accountName = account?.name || "-";

      const debit = tx.transaction_type === "debit" ? tx.amount : "";
      const credit = tx.transaction_type === "credit" ? tx.amount : "";

      totalDebit += tx.transaction_type === "debit" ? tx.amount : 0;
      totalCredit += tx.transaction_type === "credit" ? tx.amount : 0;

      const row = sheet.addRow([
        new Date(tx.transaction_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }).replace(" ", "-"),
        accountName,
        tx.description || "-",
        tx.account_code || "-",
        debit,
        credit,
      ]);

      row.eachCell((cell, colNumber) => {
        const isDebitOrCredit = colNumber === 5 || colNumber === 6;
        cell.alignment = { vertical: "middle", horizontal: isDebitOrCredit ? "right" : "left" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };

        if (isDebitOrCredit && cell.value !== "") {
          cell.numFmt = `"Rp." #,##0`;
        }
      });

      if (index % 2 === 0) {
        row.eachCell((cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFF9F9F9" },
          };
        });
      }
    });

    // Total row
    const totalRow = sheet.addRow(["", "", "TOTAL", "", totalDebit, totalCredit]);
    totalRow.eachCell((cell, colNumber) => {
      cell.font = { bold: true };
      cell.alignment = { vertical: "middle", horizontal: colNumber >= 5 ? "right" : "center" };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
      if (colNumber === 3) {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD966" } };
      }
      if ((colNumber === 5 || colNumber === 6) && cell.value !== "") {
        cell.numFmt = `"Rp." #,##0`;
      }
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `journal_${monthName.toLowerCase()}_${selectedYear}.xlsx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fetchTransactionsByMonth = async (month) => {
    setIsLoading(true);
    try {
      const startDate = `${selectedYear}-${month.start}-01`;
      const endDate = `${selectedYear}-${month.start}-${month.end}`;
      const token = localStorage.getItem("token");

      const res = await fetch(
        api(`/transactions/by-date?start=${startDate}&end=${endDate}`),
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await res.json();

      if (!data || data.length === 0) {
        alert(`Tidak ada transaksi pada bulan ${month.name} ${selectedYear}`);
        return;
      }

      exportToXLSX(data, month.name);
    } catch (err) {
      console.error("Gagal ambil data:", err);
      alert("Gagal mengambil data transaksi atau export.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchAccountCodes = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(api("/account_codes/"), {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setAccountCodes(data);
      } catch (err) {
        console.error("Error fetching account codes:", err);
      }
    };
    fetchAccountCodes();
  }, []);

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <Sidebar />
        <div className="layout-page">
          <Navbar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <h2 className="mb-4">Journal Report</h2>
              <div className="card mb-4">
                <div className="card-body">
                  <div className="row mb-4">
                    <div className="col-md-4">
                      <label className="form-label">Select Year:</label>
                      <select
                        className="form-select"
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                      >
                        {[currentYear - 1, currentYear, currentYear + 1].map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="row">
                    {getMonths(selectedYear).map((month) => (
                      <div className="col-md-3 mb-3" key={month.name}>
                        <button
                          className="btn btn-primary w-100"
                          onClick={() => fetchTransactionsByMonth(month)}
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <span
                              className="spinner-border spinner-border-sm"
                              role="status"
                              aria-hidden="true"
                            ></span>
                          ) : (
                            month.name
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>  
    </div>
  );
}
